import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Url } from "../url/url";

const toSlug = (str) =>
  str.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: [] }, { size: ["small", false, "large", "huge"] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["blockquote", "code-block"],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header","font","size","bold","italic","underline","strike",
  "color","background","list","bullet","indent","align",
  "blockquote","code-block","link","image","video",
];

const S = `
  .spe-wrap{background:#f1f5f9;min-height:100vh;padding:24px 20px}
  .spe-card{background:#fff;border-radius:12px;box-shadow:0 2px 10px rgba(0,0,0,.07);overflow:hidden;max-width:900px;margin:0 auto}
  .spe-input{width:100%;padding:9px 13px;border:1px solid #d1d5db;border-radius:7px;font-size:14px;
    outline:none;box-sizing:border-box;font-family:inherit;background:#fff;color:#1f2937;
    transition:border-color .2s,box-shadow .2s}
  .spe-input:focus{border-color:#4f46e5;box-shadow:0 0 0 3px rgba(79,70,229,.1)}
  .spe-field{display:flex;flex-direction:column;gap:6px}
  .spe-btn-save{background:#4f46e5;color:#fff;border:none;border-radius:7px;padding:10px 28px;
    font-weight:600;cursor:pointer;font-size:14px;display:inline-flex;align-items:center;gap:8px;transition:background .2s}
  .spe-btn-save:hover{background:#4338ca}
  .spe-btn-save:disabled{opacity:.6;cursor:not-allowed}
  .spe-btn-back{padding:10px 20px;border-radius:7px;border:1px solid #d1d5db;background:#fff;
    cursor:pointer;font-weight:600;font-size:14px;transition:background .15s;display:inline-flex;align-items:center;gap:6px}
  .spe-btn-back:hover{background:#f9fafb}
  .spe-btn-reset{padding:10px 20px;border-radius:7px;border:1px solid #f3a000;background:#fff;
    color:#f3a000;cursor:pointer;font-weight:600;font-size:14px;transition:all .15s}
  .spe-btn-reset:hover{background:#fff8ec}
  .spe-seo-box{background:#f8faff;border:1px solid #e0e7ff;border-radius:10px;padding:16px 18px}
  .spe-seo-ttl{font-size:11px;font-weight:700;color:#6366f1;text-transform:uppercase;letter-spacing:1px;margin:0 0 14px}
  .spe-quill .ql-container{min-height:320px;font-size:14px}
  .spe-quill .ql-editor{min-height:320px}
  .spe-quill .ql-toolbar{border-radius:7px 7px 0 0;background:#f9fafb}
  .spe-quill .ql-container{border-radius:0 0 7px 7px}
  @keyframes speIn{from{transform:translateX(50px);opacity:0}to{transform:translateX(0);opacity:1}}
  .spe-toast{position:fixed;top:20px;right:20px;z-index:9999;padding:12px 18px;border-radius:9px;
    color:#fff;font-weight:600;font-size:14px;display:flex;align-items:center;gap:10px;
    min-width:260px;box-shadow:0 8px 24px rgba(0,0,0,.18);animation:speIn .2s ease}
  .spe-header{display:flex;align-items:center;justify-content:space-between;
    padding:16px 24px;border-bottom:1px solid #f3f4f6}
  .spe-body{padding:24px}
  .spe-section-title{font-size:13px;font-weight:700;color:#374151;margin:0 0 6px}
  .spe-divider{border:none;border-top:1px solid #f3f4f6;margin:20px 0}
`;

export default function StaticPageEdit() {
  const navigate  = useNavigate();
  const { id }    = useParams();

  const EMPTY = {
    title:"", slug:"", content:"", metaTitle:"", metaKeywords:"",
    metaDescription:"", headScript:"", bodyScript:"", isActive:true,
  };

  const [form,      setForm]      = useState(EMPTY);
  const [origForm,  setOrigForm]  = useState(EMPTY); // reset ke liye original
  const [loading,   setLoading]   = useState(false);
  const [fetchLoad, setFetchLoad] = useState(true);
  const [toast,     setToast]     = useState(null);

  const showToast = useCallback((msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3200);
  }, []);

  // ── Load page data ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!id) { navigate("/dashboard/StaticPages"); return; }
    setFetchLoad(true);
    axios.get(`${Url}/api/staticpage/${id}`)
      .then(r => {
        const d = r.data.data;
        const filled = {
          title:           d.title           || "",
          slug:            d.slug            || "",
          content:         d.content         || "",
          metaTitle:       d.metaTitle       || "",
          metaKeywords:    d.metaKeywords    || "",
          metaDescription: d.metaDescription || "",
          headScript:      d.headScript      || "",
          bodyScript:      d.bodyScript      || "",
          isActive:        d.isActive ?? true,
        };
        setForm(filled);
        setOrigForm(filled); // original save karo reset ke liye
      })
      .catch(() => {
        showToast("Page data load nahi hua", false);
        setTimeout(() => navigate("/dashboard/StaticPages"), 2000);
      })
      .finally(() => setFetchLoad(false));
  }, [id]);

  const set = (name, val) => setForm(p => ({ ...p, [name]: val }));

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    set(name, type === "checkbox" ? checked : value);
  };

  const handleTitleBlur = () => {
    if (!form.slug && form.title) set("slug", toSlug(form.title));
  };

  // ── Update submit ─────────────────────────────────────────────────────────
  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.title.trim())    { showToast("Title required hai", false); return; }
    if (!form.content?.replace(/<[^>]+>/g,"").trim()) { showToast("Content required hai", false); return; }

    setLoading(true);
    try {
      await axios.put(`${Url}/api/staticpage/${id}`, form);
      showToast("Page update ho gayi ✅");
      setOrigForm(form); // naya original set karo
    } catch (err) {
      showToast(err?.response?.data?.message || "Update error aaya", false);
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable jsx-a11y/label-has-associated-control */
  return (
    <div className="spe-wrap">
      <style>{S}</style>

      {/* Toast */}
      {toast && (
        <div className="spe-toast" style={{ background: toast.ok ? "#22c55e" : "#ef4444" }}>
          {toast.ok ? "✅" : "❌"} {toast.msg}
          <button type="button" onClick={() => setToast(null)}
            style={{ marginLeft:"auto",background:"none",border:"none",color:"#fff",cursor:"pointer",fontSize:18,lineHeight:1 }}>×</button>
        </div>
      )}

      {/* Page Card */}
      <div className="spe-card">

        {/* Header */}
        <div className="spe-header">
          <div style={{ display:"flex",alignItems:"center",gap:12 }}>
            <button type="button" className="spe-btn-back"
              onClick={() => navigate("/dashboard/StaticPagesList")}>
              ← Back
            </button>
            <div>
              <h5 style={{ margin:0,fontWeight:700,fontSize:16 }}>✏️ Edit Static Page</h5>
              {form.slug && (
                <p style={{ margin:0,fontSize:12,color:"#6b7280" }}>
                  Slug: <code style={{ color:"#4f46e5" }}>{form.slug}</code>
                </p>
              )}
            </div>
          </div>

          {/* Active toggle header mein */}
          <div style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 14px",
            borderRadius:8,border:"1px solid",
            borderColor: form.isActive ? "#bbf7d0" : "#e5e7eb",
            background:  form.isActive ? "#f0fdf4"  : "#f9fafb" }}>
            <div className="form-check form-switch mb-0">
              <input className="form-check-input" type="checkbox" role="switch"
                id="spe-active" name="isActive" checked={form.isActive}
                onChange={handleChange}
                style={{ width:"2.8rem",height:"1.4rem",cursor:"pointer" }} />
              <label className="form-check-label" htmlFor="spe-active"
                style={{ fontWeight:600,color: form.isActive ? "#16a34a" : "#6b7280",cursor:"pointer" }}>
                {form.isActive ? "Active" : "Inactive"}
              </label>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="spe-body">
          {fetchLoad ? (
            <div style={{ textAlign:"center",padding:"80px 0" }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ marginTop:14,color:"#6b7280",fontSize:14 }}>Data load ho raha hai...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>

              {/* Title + Slug */}
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20 }}>
                <label className="spe-field">
                  <span className="spe-section-title">Title <span style={{ color:"#ef4444" }}>*</span></span>
                  <input type="text" className="spe-input" name="title"
                    placeholder="Page title"
                    value={form.title} onChange={handleChange} onBlur={handleTitleBlur} />
                </label>
                <label className="spe-field">
                  <span className="spe-section-title">Slug <span style={{ color:"#ef4444" }}>*</span></span>
                  <input type="text" className="spe-input" name="slug"
                    placeholder="page-url-slug"
                    value={form.slug} onChange={handleChange} />
                </label>
              </div>

              {/* Content */}
              <div style={{ marginBottom:20 }}>
                <p className="spe-section-title">
                  Content <span style={{ color:"#ef4444" }}>*</span>
                </p>
                <div className="spe-quill">
                  <ReactQuill
                    theme="snow"
                    value={form.content}
                    onChange={val => set("content", val)}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    placeholder="Page ka content yahan likhein..."
                  />
                </div>
              </div>

              <hr className="spe-divider" />

              {/* SEO */}
              <div className="spe-seo-box" style={{ marginBottom:20 }}>
                <p className="spe-seo-ttl">🔍 SEO Information</p>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14 }}>
                  <label className="spe-field">
                    <span style={{ fontSize:12,fontWeight:600,color:"#374151" }}>Meta Title</span>
                    <input type="text" className="spe-input" name="metaTitle"
                      placeholder="SEO Title" value={form.metaTitle}
                      onChange={handleChange} style={{ fontSize:13 }} />
                  </label>
                  <label className="spe-field">
                    <span style={{ fontSize:12,fontWeight:600,color:"#374151" }}>Meta Keywords</span>
                    <input type="text" className="spe-input" name="metaKeywords"
                      placeholder="keyword1, keyword2" value={form.metaKeywords}
                      onChange={handleChange} style={{ fontSize:13 }} />
                  </label>
                </div>
                <label className="spe-field" style={{ marginBottom:14 }}>
                  <span style={{ fontSize:12,fontWeight:600,color:"#374151" }}>Meta Description</span>
                  <textarea className="spe-input" name="metaDescription" rows={3}
                    placeholder="Search engines ke liye description"
                    value={form.metaDescription} onChange={handleChange}
                    style={{ resize:"vertical",fontSize:13 }} />
                </label>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14 }}>
                  <label className="spe-field">
                    <span style={{ fontSize:12,fontWeight:600,color:"#374151" }}>HEAD Script</span>
                    <textarea className="spe-input" name="headScript" rows={3}
                      placeholder="Script for &lt;head&gt; section"
                      value={form.headScript} onChange={handleChange}
                      style={{ resize:"vertical",fontSize:13,fontFamily:"monospace" }} />
                  </label>
                  <label className="spe-field">
                    <span style={{ fontSize:12,fontWeight:600,color:"#374151" }}>BODY Script</span>
                    <textarea className="spe-input" name="bodyScript" rows={3}
                      placeholder="Script for &lt;body&gt; section"
                      value={form.bodyScript} onChange={handleChange}
                      style={{ resize:"vertical",fontSize:13,fontFamily:"monospace" }} />
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display:"flex",gap:12,alignItems:"center" }}>
                <button type="submit" className="spe-btn-save" disabled={loading}>
                  {loading && (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  )}
                  Update Page
                </button>
                <button type="button" className="spe-btn-reset"
                  onClick={() => setForm(origForm)}>
                  Reset Changes
                </button>
                <button type="button" className="spe-btn-back"
                  onClick={() => navigate("/dashboard/StaticPages")}
                  style={{ marginLeft:"auto" }}>
                  Cancel
                </button>
              </div>

            </form>
          )}
        </div>
      </div>
    </div>
  );
  /* eslint-enable jsx-a11y/label-has-associated-control */
}